import { useCallback, useEffect, type MouseEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useNavigationTabs } from '@/hooks';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useGeoMarkupStore } from '@/store';
import type { Task } from '@/types';

export interface NoteNavigationPayload {
  id: string;
  shortId: string;
  title: string;
}

export function useTaskDetailsPanelNavigation(selectedTask: Task | null) {
  const {
    tabs,
    activeTabId,
    openTab,
    closeTab,
    switchTab,
    closeAllTabs,
    getTabByTaskId,
    updateTabTitle,
  } = useNavigationTabs();

  const selectTask = useGeoMarkupStore((state) => state.tasks.selectTask);
  const zoomTo = useGeoMarkupStore((state) => state.map.zoomTo);
  const { openArtifact, closeOverlay } = useArtifactNavigation();

  const tasks = useGeoMarkupStore(useShallow((state) => state.tasks.items));
  const selectNote = useGeoMarkupStore((state) => state.notes.selectNote);

  const navigateToTab = useCallback(
    (tab: { type: 'task' | 'note'; taskId: string; shortId?: string }) => {
      if (tab.type === 'note') {
        selectNote(tab.taskId);
        openArtifact('notes', 'detail', tab.shortId ?? tab.taskId);
      } else {
        selectTask(tab.taskId);

        const foundTask = tasks.find((item) => item.id === tab.taskId);
        if (foundTask?.anchorPoint) {
          zoomTo(foundTask.anchorPoint);
        }
      }
    },
    [selectTask, selectNote, openArtifact, tasks, zoomTo]
  );

  const handleTabClick = useCallback(
    (tabId: string) => {
      const freshState = useGeoMarkupStore.getState();
      const tab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === tabId
      );
      if (!tab) return;

      switchTab(tabId);
      navigateToTab(tab);
    },
    [switchTab, navigateToTab]
  );

  const handleTabClose = useCallback(
    (tabId: string, event: MouseEvent) => {
      event.stopPropagation();
      closeTab(tabId);

      const freshState = useGeoMarkupStore.getState();
      const newActiveTabId = freshState.taskNavigation.activeTabId;
      const newActiveTab = freshState.taskNavigation.tabs.find(
        (navTab) => navTab.id === newActiveTabId
      );

      if (newActiveTab) {
        navigateToTab(newActiveTab);
      } else {
        closeOverlay();
        closeAllTabs();
      }
    },
    [closeTab, navigateToTab, closeOverlay, closeAllTabs]
  );

  const handleClose = useCallback(() => {
    closeOverlay();
    closeAllTabs();
  }, [closeOverlay, closeAllTabs]);

  const handleViewRelatedTask = useCallback(
    (taskId: string, title: string) => {
      const existingTab = getTabByTaskId(taskId);

      if (existingTab) {
        handleTabClick(existingTab.id);
      } else {
        if (tabs.length === 0 && selectedTask) {
          openTab(
            selectedTask.id,
            selectedTask.title,
            'task',
            selectedTask.shortId
          );
        }

        const tabId = openTab(taskId, title);
        switchTab(tabId);
        selectTask(taskId);
      }
    },
    [
      getTabByTaskId,
      handleTabClick,
      openTab,
      switchTab,
      selectTask,
      tabs.length,
      selectedTask,
    ]
  );

  const handleViewNote = useCallback(
    ({ id, shortId, title }: NoteNavigationPayload) => {
      const existingTab = getTabByTaskId(id);

      if (existingTab) {
        switchTab(existingTab.id);
      } else {
        if (tabs.length === 0 && selectedTask) {
          openTab(
            selectedTask.id,
            selectedTask.title,
            'task',
            selectedTask.shortId
          );
        }

        openTab(id, title, 'note', shortId);
      }

      selectNote(id);
      openArtifact('notes', 'detail', shortId);
    },
    [
      getTabByTaskId,
      switchTab,
      selectNote,
      openTab,
      openArtifact,
      tabs.length,
      selectedTask,
    ]
  );

  useEffect(() => {
    if (selectedTask?.title && activeTabId) {
      const currentTab = tabs.find((navTab) => navTab.id === activeTabId);
      if (
        currentTab &&
        currentTab.type === 'task' &&
        currentTab.title !== selectedTask.title
      ) {
        updateTabTitle(activeTabId, selectedTask.title);
      }
    }
  }, [selectedTask?.title, activeTabId, tabs, updateTabTitle]);

  return {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handleClose,
    handleViewRelatedTask,
    handleViewNote,
    openArtifact,
    closeOverlay,
  };
}
